import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { Printer, ChevronLeft } from "lucide-react";
import { useDemoMode } from "@/hooks/use-demo-mode";
import { useAuth } from "@/hooks/use-auth";
import { getDataSource } from "@/lib/data-source";
import { format } from "date-fns";
import { Button } from "@/components/ui/button";
import { listCuttingTiers, listShippingMethods } from "@/lib/products";

export const Route = createFileRoute("/app/staff/jobs/$id_/ticket")({ component: JobTicket });

function JobTicket() {
  const { id } = Route.useParams();
  const { demoMode } = useDemoMode();
  const { profile } = useAuth();
  const ds = useMemo(
    () => getDataSource(profile?.role === "staff" && demoMode),
    [demoMode, profile?.role],
  );
  const { data: job } = useQuery({ queryKey: ["job", demoMode, id], queryFn: () => ds.getJob(id) });
  const { data: cuttingTiers = [] } = useQuery({
    queryKey: ["cutting-tiers"],
    queryFn: listCuttingTiers,
  });
  const { data: shippingMethods = [] } = useQuery({
    queryKey: ["shipping-methods"],
    queryFn: listShippingMethods,
  });

  const handlePrint = () => {
    window.print();
  };

  if (!job) return <div className="text-muted-foreground">Loading…</div>;

  const cuttingTier = cuttingTiers.find((t) => t.id === job.cutting_tier_id);
  const shippingMethod = shippingMethods.find((m) => m.id === job.shipping_method_id);

  return (
    <div className="min-h-screen bg-background">
      {/* Non-printable header */}
      <div className="border-b border-border bg-card p-4 print:hidden">
        <div className="mx-auto max-w-4xl flex items-center justify-between">
          <Link
            to="/app/staff/jobs/$id"
            params={{ id }}
            className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground"
          >
            <ChevronLeft className="h-4 w-4" /> Back to job
          </Link>
          <Button
            onClick={handlePrint}
            className="rounded-full bg-gradient-hero text-primary-foreground shadow-soft hover:opacity-90"
          >
            <Printer className="h-4 w-4 mr-2" /> Print
          </Button>
        </div>
      </div>

      {/* Printable ticket */}
      <div className="mx-auto max-w-4xl p-8 print:p-0">
        <div className="rounded-3xl border border-border bg-card p-8 shadow-soft print:shadow-none print:border-0 print:p-0 print:bg-white">
          {/* Ticket Header */}
          <div className="border-b border-border pb-6 mb-6 print:pb-4 print:mb-4">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-2xl font-bold print:text-xl">FlightDeck Job Ticket</h1>
                <p className="text-sm text-muted-foreground">Resolute Trade Print</p>
              </div>
              <div className="text-right">
                <div className="text-sm text-muted-foreground">Job ID</div>
                <div className="font-mono font-semibold">{job.id.slice(0, 8)}</div>
              </div>
            </div>
          </div>

          {/* Job Info */}
          <div className="grid gap-6 sm:grid-cols-2 mb-8">
            <div>
              <h2 className="font-semibold text-sm uppercase tracking-wide text-muted-foreground mb-3">
                Job Details
              </h2>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Title:</span>
                  <span className="font-medium">{job.gang_sheet_name || job.job_title}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Status:</span>
                  <span className="font-medium">{job.status}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Due date:</span>
                  <span className="font-medium">
                    {job.due_date ? format(new Date(job.due_date), "d MMM yyyy") : "—"}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Created:</span>
                  <span className="font-medium">
                    {format(new Date(job.created_at), "d MMM yyyy")}
                  </span>
                </div>
              </div>
            </div>

            <div>
              <h2 className="font-semibold text-sm uppercase tracking-wide text-muted-foreground mb-3">
                Customer
              </h2>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Company:</span>
                  <span className="font-medium">{job.customer?.company_name || "—"}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Contact:</span>
                  <span className="font-medium">{job.customer?.contact_name || "—"}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Email:</span>
                  <span className="font-medium">{job.customer?.email || "—"}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Product & Specs */}
          <div className="border-t border-border pt-6 mb-8 print:pt-4 print:mb-4">
            <h2 className="font-semibold text-sm uppercase tracking-wide text-muted-foreground mb-3">
              Product & Specifications
            </h2>
            <div className="grid gap-4 sm:grid-cols-2 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Product:</span>
                <span className="font-medium">{job.product_type ?? "—"}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Metres / Quantity:</span>
                <span className="font-medium">{String(job.quantity ?? 0)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Print size:</span>
                <span className="font-medium">{job.print_size ?? "—"}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Colours:</span>
                <span className="font-medium">{job.colours ?? "—"}</span>
              </div>
              {cuttingTier && (
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Cutting tier:</span>
                  <span className="font-medium">
                    {cuttingTier.name} @ £{cuttingTier.price_per_m.toFixed(2)}/m
                  </span>
                </div>
              )}
              {shippingMethod && (
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Shipping:</span>
                  <span className="font-medium">{shippingMethod.name}</span>
                </div>
              )}
              <div className="flex justify-between">
                <span className="text-muted-foreground">Payment status:</span>
                <span className="font-medium capitalize">{job.payment_status ?? "pending"}</span>
              </div>
            </div>
          </div>

          {/* Order Financials */}
          <div className="border-t border-border pt-6 mb-8 print:pt-4 print:mb-4">
            <h2 className="font-semibold text-sm uppercase tracking-wide text-muted-foreground mb-3">
              Order Financials
            </h2>
            <div className="grid gap-4 sm:grid-cols-2 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">VAT:</span>
                <span className="font-medium">
                  {job.vat_amount ? `£${Number(job.vat_amount).toFixed(2)}` : "—"}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Shipping cost:</span>
                <span className="font-medium">
                  {job.shipping_cost ? `£${Number(job.shipping_cost).toFixed(2)}` : "—"}
                </span>
              </div>
              <div className="flex justify-between sm:col-span-2">
                <span className="text-muted-foreground font-semibold">Total:</span>
                <span className="font-bold">
                  {job.total_cost ? `£${Number(job.total_cost).toFixed(2)}` : "—"}
                </span>
              </div>
            </div>
          </div>

          {/* Address Details */}
          <div className="border-t border-border pt-6 mb-8 print:pt-4 print:mb-4">
            <h2 className="font-semibold text-sm uppercase tracking-wide text-muted-foreground mb-3">
              Address Details
            </h2>
            <div className="grid gap-4 sm:grid-cols-2 text-sm">
              {job.shipping_address && (
                <div>
                  <div className="text-muted-foreground mb-1">Shipping:</div>
                  <div className="font-medium whitespace-pre-wrap">{job.shipping_address}</div>
                </div>
              )}
              {job.billing_address && (
                <div>
                  <div className="text-muted-foreground mb-1">Billing:</div>
                  <div className="font-medium whitespace-pre-wrap">{job.billing_address}</div>
                </div>
              )}
            </div>
          </div>

          {/* Gang Sheet Status */}
          <div className="border-t border-border pt-6 mb-8 print:pt-4 print:mb-4">
            <h2 className="font-semibold text-sm uppercase tracking-wide text-muted-foreground mb-3">
              Gang Sheet
            </h2>
            <div className="text-sm">
              <div className="flex justify-between mb-2">
                <span className="text-muted-foreground">Status:</span>
                <span
                  className={`font-medium ${job.gangsheet_design_id ? "text-green-600 dark:text-green-400" : "text-amber-600 dark:text-amber-400"}`}
                >
                  {job.gangsheet_design_id ? "Built" : "Missing"}
                </span>
              </div>
              {job.gangsheet_design_id && (
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Design ID:</span>
                  <span className="font-mono font-medium">{job.gangsheet_design_id}</span>
                </div>
              )}
            </div>
          </div>

          {/* Notes section for handwriting */}
          <div className="border-t border-border pt-6 print:pt-4">
            <h2 className="font-semibold text-sm uppercase tracking-wide text-muted-foreground mb-3">
              Notes
            </h2>
            <div className="min-h-[200px] border border-dashed border-border rounded-2xl p-4 bg-secondary/20">
              <p className="text-sm text-muted-foreground italic">Space for handwritten notes...</p>
            </div>
          </div>

          {/* Footer */}
          <div className="mt-8 pt-6 border-t border-border text-center text-xs text-muted-foreground print:mt-4 print:pt-4">
            <p>Generated by FlightDeck · {new Date().toLocaleString()}</p>
          </div>
        </div>
      </div>

      {/* Print styles */}
      <style>{`
        @media print {
          @page {
            size: A4;
            margin: 0;
          }
          body {
            background: white !important;
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
          }
          * {
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
          }
          .print\\:hidden {
            display: none !important;
          }
          .print\\:shadow-none {
            box-shadow: none !important;
          }
          .print\\:border-0 {
            border: none !important;
          }
          .print\\:p-0 {
            padding: 0 !important;
          }
          .print\\:bg-white {
            background: white !important;
          }
          .print\\:pb-4 {
            padding-bottom: 1rem !important;
          }
          .print\\:mb-4 {
            margin-bottom: 1rem !important;
          }
          .print\\:pt-4 {
            padding-top: 1rem !important;
          }
          .print\\:text-xl {
            font-size: 1.25rem !important;
          }
          /* Hide all navigation, sidebars, and mobile elements */
          nav, header, footer, aside, .sidebar, .mobile-nav {
            display: none !important;
          }
          /* Ensure only the ticket is printed */
          > *:not([class*="min-h-screen"]) {
            display: none !important;
          }
          .min-h-screen > *:not([class*="max-w-4xl"]) {
            display: none !important;
          }
        }
      `}</style>
    </div>
  );
}
